import { Button } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import CardTitle from "@saleor/components/CardTitle";
import { Hr } from "@saleor/components/Hr";
import Skeleton from "@saleor/components/Skeleton";
import useUser from "@saleor/hooks/useUser";
import { maybe, transformPaymentStatus } from "@saleor/misc";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import { getPaymentChannelCode } from "@saleor/utils/paymentChannelCode/paymentChannelCode";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { StoreOrderDetails_storeOrder } from "../../types/StoreOrderDetails";
import StoreOrderGCashTooltip from "../StoreOrderGCashTooltip";

const useStyles = makeStyles(
  theme => ({
    cancelButton: {
      "& span": {
        color: "#E26139"
      },
      border: "1px solid #E26139"
    },
    cancelButtonDisabled: {
      "& span": {
        color: "#d1d1d1"
      },
      border: "1px solid #d1d1d1"
    },
    root: {
      ...theme.typography.body1,
      lineHeight: 1.9,
      width: "100%"
    },
    textBold: {
      fontWeight: 600
    },
    textRight: {
      textAlign: "right"
    }
  }),
  { name: "StoreOrderPayment" }
);

interface StoreOrderPaymentProps {
  orderInfoComponent?: React.ReactNode;
  storeOrder: StoreOrderDetails_storeOrder;
  orderStatus: string;
  disableAction: boolean;
  enableManageMallActions: boolean;
  onCancel();
}

const StoreOrderPayment: React.FC<StoreOrderPaymentProps> = props => {
  const {
    orderInfoComponent,
    storeOrder,
    orderStatus,
    onCancel,
    disableAction,
    enableManageMallActions
  } = props;
  const classes = useStyles(props);
  const intl = useIntl();

  const user = useUser();
  const isStoreAdmin =
    user.user?.userType?.code === SmopUserTypeCode.STORE_ADMIN;
  const isOpsAdmin = user.user?.userType?.code === SmopUserTypeCode.OMI_OPS;
  const isNotCanceledOrCompleted =
    orderStatus !== "CANCELED" && orderStatus !== "COMPLETED";
  let userCanCancel = isStoreAdmin || isOpsAdmin || enableManageMallActions;
  userCanCancel = isStoreAdmin ? orderStatus !== "RETURN_TO_FC" : userCanCancel;

  const formatter = new Intl.NumberFormat("en-PH", {
    currency: "PHP",
    currencyDisplay: "symbol",
    minimumFractionDigits: 2,
    style: "currency"
  });

  const payment = transformPaymentStatus(
    maybe(() => storeOrder.order.payments[0].chargeStatus),
    intl
  );

  return (
    <Card>
      {maybe(
        () =>
          (isStoreAdmin || isOpsAdmin) &&
          orderStatus !== "CANCELED" &&
          orderStatus !== "COMPLETED" &&
          storeOrder?.order?.payments[0]?.paymentMethodType === "gcash"
      ) ? (
        <StoreOrderGCashTooltip
          visible={false} // Set to true to show GCash Tool tip
        />
      ) : null}
      <CardTitle
        title="Order Info & Payment"
        toolbar={
          userCanCancel && isNotCanceledOrCompleted ? (
            <Button
              id="fulfillmentCancelBtn"
              onClick={onCancel}
              className={
                disableAction
                  ? classes.cancelButtonDisabled
                  : classes.cancelButton
              }
              variant="outlined"
              disabled={disableAction}
            >
              <FormattedMessage
                defaultMessage="Cancel Order"
                description="button"
              />
            </Button>
          ) : null
        }
      />
      {/* Order Info */}
      {orderInfoComponent}
      <Hr />
      {/* Payment Details */}
      <CardContent
        style={{
          paddingBottom: "3px",
          paddingTop: "5px"
        }}
      >
        <table className={classes.root}>
          <tbody>
            <tr>
              <td>
                {storeOrder?.order?.subOrders ? (
                  <span>
                    <b>Sub-total (1 Seller)</b>
                  </span>
                ) : (
                  <Skeleton />
                )}
              </td>

              <td className={classes.textRight}>
                {storeOrder?.totalAmount ? (
                  <span>{formatter.format(storeOrder.subtotal)}</span>
                ) : (
                  <Skeleton />
                )}
              </td>
            </tr>
            <tr>
              <td>
                <span>
                  <b>Voucher Discount</b>
                </span>{" "}
                {storeOrder?.order?.voucher ? (
                  <span>
                    <b>({storeOrder.order.voucher.code})</b>
                  </span>
                ) : null}
              </td>
              <td className={classes.textRight}>
                {storeOrder?.discountAmount ? (
                  <span>-{formatter.format(storeOrder.discountAmount)}</span>
                ) : (
                  "-"
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </CardContent>
      <Hr />
      <CardContent>
        <table className={classes.root}>
          <tbody>
            <tr>
              <td>
                <b>
                  <FormattedMessage
                    defaultMessage="Total"
                    description="order total price"
                  />
                </b>
              </td>
              <td className={classes.textRight}>
                {storeOrder?.totalAmount ? (
                  <span>
                    {formatter.format(parseFloat(storeOrder.totalAmount))}
                  </span>
                ) : (
                  <Skeleton />
                )}
              </td>
            </tr>
            <tr>
              <td>
                <b>
                  <FormattedMessage
                    defaultMessage="Payment Mode"
                    description="order payment"
                  />
                </b>
              </td>
              <td className={classes.textRight}>
                {maybe(() => storeOrder.order.payments[0].paymentMethodType) ? (
                  <span>{getPaymentChannelCode(storeOrder.order)}</span>
                ) : (
                  <Skeleton />
                )}
              </td>
            </tr>
            <tr>
              <td>
                <b>
                  <FormattedMessage
                    defaultMessage="Payment Status"
                    description="order payment"
                  />
                </b>
              </td>
              <td className={classes.textRight}>
                {storeOrder?.order?.payments ? (
                  <span>{payment.localized.toUpperCase()}</span>
                ) : (
                  <Skeleton />
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};
StoreOrderPayment.displayName = "StoreOrderPayment";
export default StoreOrderPayment;
